/**
 * Top-level content tabs (Age Assurance / Digital IDs / EUDI Wallets): persists
 * the choice, syncs the toggle UI and filter visibility, resets filters on a
 * user-initiated switch, and re-renders the globe + table when the active tab
 * changes.
 *
 * "EUDI Wallets" is not its own data type — it is the Digital IDs data type
 * with the EUDI Wallet colour overlay and legend on — so the tab and the data
 * type are tracked separately: `#data-type-toggle[data-tab]` holds the tab and
 * `#filter-section[data-data-type]` / `#data-type-toggle[data-value]` hold the
 * data type everything else reads.
 *
 * The table filter is injected lazily via getTableFilter() because it is created
 * after this wiring runs; on initial load the getter returns null, matching the
 * monolith's var-hoisting behavior where the initial setTab() dispatch found no
 * table filter yet.
 */
/* eslint-disable */
import { getDataType } from '../data-type'
import type { EudiOverlayApi } from './eudi-overlay'
import type { TableFilterApi } from './table-filter'

const DATA_TYPE_KEY = 'atlas-data-type'

export type ContentTab = 'age_assurance' | 'digital_ids' | 'eudi_wallets'

/** The data type a tab renders; the EUDI Wallets tab is a Digital IDs view. */
export function dataTypeForTab(tab: string): string {
  return tab === 'digital_ids' || tab === 'eudi_wallets' ? 'digital_ids' : 'age_assurance'
}

// Persisted preference from localStorage (distinct from the imported
// getDataType(), which reads the live DOM selection).
export function getStoredTab(): ContentTab {
  try {
    var stored = localStorage.getItem(DATA_TYPE_KEY)
    if (stored === 'digital_ids' || stored === 'age_assurance' || stored === 'eudi_wallets') {
      return stored
    }
  } catch (err) {}
  return 'age_assurance'
}

export function getStoredDataType(): string {
  return dataTypeForTab(getStoredTab())
}

export interface DataTypeToggleDeps {
  eudiOverlay: EudiOverlayApi
  getTableFilter: () => TableFilterApi | null
}

export function initDataTypeToggle(deps: DataTypeToggleDeps): void {
    var dataTypeToggle = document.getElementById('data-type-toggle');
    var dataTypeAgeAssuranceBtn = document.getElementById('data-type-age-assurance-btn');
    var dataTypeDigitalIdsBtn = document.getElementById('data-type-digital-ids-btn');
    var dataTypeEudiWalletsBtn = document.getElementById('data-type-eudi-wallets-btn');
    var filterSection = document.getElementById('filter-section');

    function setActive(btn, isActive) {
      if (!btn) return;
      btn.classList.toggle('active', isActive);
      btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    }

    function getSelectedTab() {
      return (dataTypeToggle && dataTypeToggle.getAttribute('data-tab')) || 'age_assurance';
    }

    function setTab(tab) {
      var dataType = dataTypeForTab(tab);
      setActive(dataTypeAgeAssuranceBtn, tab === 'age_assurance');
      setActive(dataTypeDigitalIdsBtn, tab === 'digital_ids');
      setActive(dataTypeEudiWalletsBtn, tab === 'eudi_wallets');
      if (filterSection) filterSection.setAttribute('data-data-type', dataType);
      if (dataTypeToggle) {
        dataTypeToggle.setAttribute('data-value', dataType);
        dataTypeToggle.setAttribute('data-tab', tab);
      }
      try {
        localStorage.setItem(DATA_TYPE_KEY, tab);
      } catch (err) {}
      // Set before the change event so the globe repaint and table re-render it
      // triggers already see the overlay state this tab implies.
      deps.eudiOverlay.setActive(tab === 'eudi_wallets');
      if (typeof window.dispatchEvent === 'function') {
        window.dispatchEvent(new CustomEvent('atlas-data-type-change', { detail: { dataType: dataType, tab: tab } }));
      }
    }

    // Age Assurance-only filters (tags, enactment status) are
    // hidden for Digital IDs, where they are irrelevant.
    function applyDataTypeFilterVisibility(type) {
      var isDigitalIds = type === 'digital_ids';
      var legGroup = document.getElementById('legislation-tag-filter-group');
      var statusGroup = document.getElementById('status-filter-group');
      if (legGroup) legGroup.style.display = isDigitalIds ? 'none' : '';
      if (statusGroup) statusGroup.style.display = isDigitalIds ? 'none' : '';
      // All Digital ID data is national, so the jurisdiction-level select is
      // meaningless there; hide it (resetForTabSwitch resets it to "All").
      var levelGroup = document.getElementById('jurisdiction-level-filter-group');
      if (levelGroup) levelGroup.style.display = isDigitalIds ? 'none' : '';
      // Level of assurance + Claims returned filters only apply to Digital IDs.
      var loaGroup = document.getElementById('loa-filter-group');
      var claimsGroup = document.getElementById('claims-filter-group');
      if (loaGroup) loaGroup.style.display = isDigitalIds ? '' : 'none';
      if (claimsGroup) claimsGroup.style.display = isDigitalIds ? '' : 'none';
    }

    // Side-effect-light data-type setter for deep links (e.g. the drawer
    // page-load handler opening a shared Digital IDs URL). Unlike the toggle
    // buttons, this does not reset filters or close the drawer.
    window.setAtlasDataType = function(type) {
      setTab(type);
      applyDataTypeFilterVisibility(dataTypeForTab(type));
    };

    // Reset filters and the view so the new tab starts from a clean slate.
    function resetForTabSwitch() {
      if (typeof window.closeDrawer === 'function') window.closeDrawer();

      var searchInputEl = document.getElementById('search-input') as HTMLInputElement | null;
      if (searchInputEl) searchInputEl.value = '';

      var levelSelect = document.getElementById('jurisdiction-level-select') as HTMLInputElement | null;
      if (levelSelect) levelSelect.value = '';
      try { localStorage.setItem('regulationLevel', 'platform'); } catch (err) {}
      if (window.globe) window.globe.setRegulationLevel('platform');

      // Re-select every value in each multi-select filter combobox (status,
      // tags, level of assurance, claims) so the new tab starts from "All".
      var tableFilter = deps.getTableFilter();
      if (tableFilter) tableFilter.resetStatusFilter();
      if (tableFilter) tableFilter.resetDigitalIdFilters();
    }

    // User-initiated tab change: update state, reset filters/view, sync UI.
    function switchTab(tab) {
      if (getSelectedTab() === tab) return;
      resetForTabSwitch();
      setTab(tab);
      applyDataTypeFilterVisibility(dataTypeForTab(tab));
    }

    if (dataTypeAgeAssuranceBtn) {
      dataTypeAgeAssuranceBtn.addEventListener('click', function() { switchTab('age_assurance'); });
    }
    if (dataTypeDigitalIdsBtn) {
      dataTypeDigitalIdsBtn.addEventListener('click', function() { switchTab('digital_ids'); });
    }
    if (dataTypeEudiWalletsBtn) {
      dataTypeEudiWalletsBtn.addEventListener('click', function() { switchTab('eudi_wallets'); });
    }
    window.addEventListener('atlas-data-type-change', function() {
      if (window.globe) {
        window.globe.applyModeColors(true);
        window.globe.applyGlobeStyle(window.globe.getCurrentDataType(), true);
      }
      var dataType = getDataType();
      var tableFilter = deps.getTableFilter();
      if (tableFilter) tableFilter.renderTableBody(dataType);
    });

    setTab(getStoredTab());
    applyDataTypeFilterVisibility(getStoredDataType());
}
